import { restyleShowroomStyle } from "./showroom-products";
import {
  getShowroomConfig,
  refreshFromPage,
  showroomRequest,
  showroomToast,
  toNumericId,
  updateShowroomState,
} from "./showroom-api";

// Each color is its own product, so "Change" opens the wishlist drawer's color
// picker on the style's product group (colors already in the list are excluded).

class ShowroomClosetCard extends HTMLElement {
  connectedCallback() {
    this.page = this.closest("showroom-page");

    this.addEventListener("click", (event) => {
      if (event.target.closest("[data-closet-remove]")) this.removeStyle();
      else if (event.target.closest("[data-closet-remove-from-list]")) this.removeFromList();
      else if (event.target.closest("[data-closet-colors-open]")) this.openColors();
    });
  }

  get email() {
    return this.dataset.email;
  }

  request(endpoint, body) {
    return showroomRequest(endpoint, { body, showroom: this.page?.dataset.showroomId });
  }

  async removeStyle() {
    this.classList.add("is-loading");

    const variant = this.dataset.variantId;
    const { ok } = await this.request("removeProductVariantFromShowroom", {
      variant,
      email: this.email,
      member_id: this.dataset.memberId,
    });

    if (!ok) {
      this.classList.remove("is-loading");
      showroomToast(this.page?.dataset.localeGenericError);
      return;
    }

    // Removed: take it off the member's list, and their lineup column, now.
    // Removing a style also drops it from the lineup, so the whole lineup tab
    // (and the export) is refreshed once the page no longer has it.
    const memberId = this.dataset.memberId || "";
    const page = this.page;
    const lineupSlide = page
      ?.querySelector(`[data-lineup-card][data-member-id="${CSS.escape(memberId)}"]`)
      ?.querySelector(`[data-slide][data-variant-id="${CSS.escape(String(variant))}"]`);
    if (lineupSlide) page.removeLineupSlide?.(lineupSlide.closest("[data-lineup-card]"), lineupSlide);
    updateShowroomState((state) => ({
      ...state,
      lineup: (state.lineup || []).filter((item) => !(item.member === memberId && String(item.variant) === String(variant))),
    }));
    (this.closest("li") || this).remove();
    page?.syncLineupButtons?.();

    await refreshFromPage(["[data-showroom-state]", "#showroom-panel-lineup", "#showroom-modal-export [data-export-canvas]"], {
      isReady: (doc) =>
        !doc.querySelector(`showroom-closet-card[data-member-id="${CSS.escape(memberId)}"][data-variant-id="${variant}"]`),
    });
  }

  // A showroom style (the owner's "Showroom styles" list): takes it out of
  // the showroom's style list. Members' own styles and lineups keep it.
  async removeFromList() {
    const { productId, variantId } = this.dataset;
    const item = this.closest("li") || this;

    this.classList.add("is-loading");

    const { ok } = await this.request("removeProductVariantFromShowroomList", {
      variant: variantId || "",
      product: productId || "",
    });

    if (!ok) {
      this.classList.remove("is-loading");
      showroomToast(this.page?.dataset.localeGenericError);
      return;
    }

    const list = item.parentElement;
    item.remove();
    list?.closest("showroom-row-slider")?.updateArrows?.();
    updateShowroomState((state) => ({
      ...state,
      styles: (state.styles || []).filter((id) => String(id) !== String(variantId)),
    }));

    const selector = productId
      ? `.showroom-closet-card--picker[data-product-id="${CSS.escape(productId)}"]`
      : `.showroom-closet-card--picker[data-variant-id="${CSS.escape(variantId || "")}"]`;
    await refreshFromPage(["[data-showroom-state]", "#showroom-panel-lineup"], {
      isReady: (doc) => !doc.querySelector(selector),
    });
  }

  // Opens the color picker on this style's colors; the chosen one replaces it.
  openColors() {
    // A change still being saved: wait for it, so the next one starts from
    // the color the member now has.
    if (this.classList.contains("is-loading")) return;

    const { productId, productGroupId } = this.dataset;
    if (!productId || !productGroupId) {
      showroomToast(this.page?.dataset.localeGenericError);
      return;
    }

    const { strings = {} } = getShowroomConfig();
    const image = this.querySelector("[data-style-image] img");

    const detail = {
      productId: `gid://shopify/Product/${productId}`,
      productGroupId,
      color: this.dataset.colorLabel || "",
      image: image?.currentSrc || image?.src || "",
      imageAlt: image?.alt || "",
      badges: [],
      swymProductId: productId,
      swymVariantId: this.dataset.variantId,
      addLabel: this.dataset.updateLabel,
      onSelect: (selection) => this.saveColor(selection),
    };

    // The drawer closes once a color is picked; a taken color says so.
    this.alreadyAddedMessage = strings.alreadyAdded;
    document.dispatchEvent(new CustomEvent("drawer:before-open", { detail: { name: "wishlist" } }));
    document.dispatchEvent(new CustomEvent("wishlist:open", { detail }));
  }

  async saveColor(selection) {
    const productId = toNumericId(selection?.productId);
    const variantId = toNumericId(selection?.variantId);

    // The same color: nothing to change.
    if (!variantId || productId === this.dataset.productId) return;

    if ((this.dataset.closetProductIds || "").includes(`,${productId},`)) {
      showroomToast(this.alreadyAddedMessage);
      return;
    }

    this.classList.add("is-loading");

    const { ok } = await this.request("updateProductVariantInShowroom", {
      variant: String(variantId),
      // What's stored for the style: an imported style may have only a
      // product (data-variant-id is then filled in for display with the
      // product's first variant, which the app has no record of).
      originalVariant: this.dataset.storedVariantId || "",
      originalProduct: this.dataset.productId || "",
      email: this.email,
      member_id: this.dataset.memberId,
    });

    if (!ok) {
      this.classList.remove("is-loading");
      showroomToast(this.page?.dataset.localeGenericError);
      return;
    }

    // Saved: show the new color now — on this card and on the member's
    // lineup column — so a further change starts from it (changing again
    // from the old color would ask the app for a style the member no longer
    // has). The storefront takes a few seconds to a minute to serve the
    // change; the page is refreshed in place once it does, without a reload.
    const memberId = this.dataset.memberId || "";
    const oldProductId = this.dataset.productId || "";
    const oldVariantId = this.dataset.variantId || "";
    const next = { productId, variantId };

    const lineupCard = this.page?.querySelector(`[data-lineup-card][data-member-id="${CSS.escape(memberId)}"]`);
    const slides = [...(lineupCard?.querySelectorAll("[data-slide]") || [])].filter(
      (slide) =>
        (oldProductId && slide.dataset.productId === oldProductId) ||
        (oldVariantId && slide.dataset.variantId === oldVariantId),
    );

    // The member's other cards know which colors they have (for "Change").
    (this.closest("[data-row-track]") || this)
      .querySelectorAll("showroom-closet-card[data-closet-product-ids]")
      .forEach((card) => {
        card.dataset.closetProductIds = card.dataset.closetProductIds.replace(`,${oldProductId},`, `,${productId},`);
      });
    updateShowroomState((state) => ({
      ...state,
      lineup: (state.lineup || []).map((item) =>
        item.member === memberId && String(item.variant) === String(oldVariantId) ? { ...item, variant: String(variantId) } : item,
      ),
    }));

    await Promise.all([restyleShowroomStyle(this, next), ...slides.map((slide) => restyleShowroomStyle(slide, next))]);
    this.classList.remove("is-loading");
    this.page?.syncLineupButtons?.();

    await refreshFromPage(["[data-showroom-state]", "#showroom-panel-lineup", "#showroom-modal-export [data-export-canvas]"], {
      isReady: (doc) =>
        !!doc.querySelector(
          `showroom-closet-card[data-member-id="${CSS.escape(memberId)}"][data-product-id="${CSS.escape(String(productId))}"]`,
        ),
    });
  }
}

if (!customElements.get("showroom-closet-card")) {
  customElements.define("showroom-closet-card", ShowroomClosetCard);
}
